import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Divider,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useRole } from '../contexts/RoleContext';
import { getApiUrl } from '../utils/api';

interface UserProfileData {
  id: number;
  email: string;
  full_name: string;
  phone_number: string;
  role: string;
  is_active: boolean;
  timezone: string;
  created_at: string;
}

const UserProfile: React.FC = () => {
  const apiUrl = getApiUrl();
  const { token } = useAuth();
  const { isEndUser } = useRole();
  const [profile, setProfile] = useState<UserProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    full_name: '',
    phone_number: '',
    timezone: 'America/Los_Angeles',
  });

  useEffect(() => {
    if (isEndUser) {
      fetchUserProfile();
    }
  }, [isEndUser]);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiUrl}/auth/test-token`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        setFormData({
          full_name: data.full_name || '',
          phone_number: data.phone_number || '',
          timezone: data.timezone || 'America/Los_Angeles',
        });
      } else {
        setError('Failed to fetch user profile');
      }
    } catch (err) {
      setError('An error occurred while fetching profile');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const response = await fetch(`${apiUrl}/users/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSuccess('Profile updated successfully!');
        // Refresh the profile data
        fetchUserProfile();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to update profile');
      }
    } catch (err) {
      setError('An error occurred while updating profile');
    } finally {
      setSaving(false);
    }
  };

  if (!isEndUser) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Access denied. This page is only available for end users.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        My Profile
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Manage your personal information and preferences.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Personal Information
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email Address"
                value={profile?.email || ''}
                fullWidth
                InputProps={{ readOnly: true }}
                helperText="Email cannot be changed"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Role"
                value={profile?.role || ''}
                fullWidth
                InputProps={{ readOnly: true }}
                helperText="Role is assigned by administrator"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Full Name"
                value={formData.full_name}
                onChange={handleInputChange('full_name')}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone Number"
                value={formData.phone_number}
                onChange={handleInputChange('phone_number')}
                fullWidth
                placeholder="+1234567890"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Timezone"
                value={formData.timezone}
                onChange={handleInputChange('timezone')}
                fullWidth
                select
                SelectProps={{ native: true }}
              >
                <option value="America/Los_Angeles">Pacific Time (Los Angeles)</option>
                <option value="America/Denver">Mountain Time (Denver)</option>
                <option value="America/Chicago">Central Time (Chicago)</option>
                <option value="America/New_York">Eastern Time (New York)</option>
                <option value="UTC">UTC</option>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Status"
                value={profile?.is_active ? 'Active' : 'Inactive'}
                fullWidth
                InputProps={{ readOnly: true }}
                helperText="Status is managed by administrator"
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              size="large"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default UserProfile;
